import {
  Carousel,
  LayoutGallery,
  LayoutSplit,
  LayoutTwoColumnsSplit,
  MasonaryGrid,
  Slider,
  Thumbnails,
} from '@wix/wix-ui-icons-common';
import React, { useContext } from 'react';

import { RicosContext } from './../../ricos-context';
import { GalleryOptions_Layout_Type as GALLERY_LAYOUT } from './../../ricos-schema/next-schema';
import { KEYS_CHARCODE } from './../../editor-common';
import type { SupportedLayouts } from './../common-types';
import { DropdownModal, ListItemSection, ListItemSelect } from './../../toolbars-ui';

import {
  LAYOUT_DROPDOWN_OPTION,
  LAYOUT_DROPDOWN_OPTION_CUSTOMIZE_LAYOUT,
  LAYOUT_SELECTION_DROPDOWN,
} from '../data-hooks';

type Props = {
  selectedLayout: SupportedLayouts;
  onLayoutSelect: (value: SupportedLayouts) => void;
  onCustomizeButtonClick: (params: { clickFromKeyboard: boolean }) => void;
};

export const LayoutDropdown: React.FC<Props> = ({
  selectedLayout,
  onLayoutSelect,
  onCustomizeButtonClick,
}) => {
  const { t, jsdomMode } = useContext(RicosContext) || {};

  const handleOnKeyDown = (e: $TSFixMe, onClick: $TSFixMe) => {
    if (e.keyCode === KEYS_CHARCODE.ENTER) {
      onClick();
      e.stopPropagation();
    }
  };

  return (
    <DropdownModal
      options={[
        ...Object.entries(dropdownOptions).map(([layout, { icon: Icon, text }]) => (
          <ListItemSelect
            key={layout}
            dataHook={`${LAYOUT_DROPDOWN_OPTION}${layout}`}
            prefix={<Icon />}
            title={t(text)}
            selected={layout === selectedLayout}
            onClick={() => onLayoutSelect(layout as SupportedLayouts)}
            onKeyDown={e => handleOnKeyDown(e, () => onLayoutSelect(layout as SupportedLayouts))}
          />
        )),
        <>
          <ListItemSection type={'divider'} />
          <ListItemSelect
            title={t('GalleryPlugin_Layouts_Customize_Button')}
            onClick={e => onCustomizeButtonClick({ clickFromKeyboard: !e.detail })}
            onKeyDown={e => handleOnKeyDown(e, onCustomizeButtonClick)}
            dataHook={LAYOUT_DROPDOWN_OPTION_CUSTOMIZE_LAYOUT}
          />
        </>,
      ]}
      jsdomMode={jsdomMode}
      dataHook={LAYOUT_SELECTION_DROPDOWN}
    />
  );
};

export const dropdownOptions: Record<
  SupportedLayouts,
  { text: string; tooltip: string; icon: React.FC }
> = {
  [GALLERY_LAYOUT.GRID]: {
    text: 'GalleryPlugin_Layout_Grid',
    tooltip: 'GalleryPlugin_Layout_Grid',
    icon: LayoutGallery,
  },
  [GALLERY_LAYOUT.MASONRY]: {
    text: 'GalleryPlugin_Layout_Masonry',
    tooltip: 'GalleryPlugin_Layout_Masonry',
    icon: MasonaryGrid,
  },
  [GALLERY_LAYOUT.COLLAGE]: {
    text: 'GalleryPlugin_Layout_Collage',
    tooltip: 'GalleryPlugin_Layout_Collage',
    icon: props => (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          transform: 'rotate(90deg)',
        }}
      >
        <MasonaryGrid {...props} />
      </div>
    ),
  },
  [GALLERY_LAYOUT.THUMBNAIL]: {
    text: 'GalleryPlugin_Layout_Thumbnails',
    tooltip: 'GalleryPlugin_Layout_Thumbnails',
    icon: Thumbnails,
  },
  [GALLERY_LAYOUT.FULLSIZE]: {
    text: 'GalleryPlugin_Layout_Slideshow',
    tooltip: 'GalleryPlugin_Layout_Slideshow',
    icon: Carousel,
  },
  [GALLERY_LAYOUT.PANORAMA]: {
    text: 'GalleryPlugin_Layout_Panorama',
    tooltip: 'GalleryPlugin_Layout_Panorama',
    icon: LayoutSplit,
  },
  [GALLERY_LAYOUT.COLUMN]: {
    text: 'GalleryPlugin_Layout_Columns',
    tooltip: 'GalleryPlugin_Layout_Columns',
    icon: LayoutTwoColumnsSplit,
  },
  [GALLERY_LAYOUT.SLIDER]: {
    text: 'GalleryPlugin_Layout_Slides',
    tooltip: 'GalleryPlugin_Layout_Slides',
    icon: Slider,
  },
};
